<script setup lang="ts">
import { format, parseISO } from 'date-fns';
import { ExternalLink } from 'lucide-vue-next';
import { ref, watch } from 'vue';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table';
import { openExternalLink } from '@/lib/commonFunctions';
import {
    getInvoices,
    getSubscriptionStatus,
    type InvoiceData
} from '@/services/billing';
import { useAccessContextStore } from '@/stores/accessContextStore';
import { useStripeStore } from '@/stores/stripeStore';
import { useTenantStore } from '@/stores/tenant';
import { SubscriptionPlan } from '@/types';

type InvoiceItem = {
    id: string;
    label: string;
    statusLabel: string;
    amountLabel: string;
    stripeUrl: string;
};

defineProps<{
    currentSubscriptionPlan: SubscriptionPlan;
}>();

const tenantStore = useTenantStore();
const accessContextStore = useAccessContextStore();
const stripeStore = useStripeStore();

const paidSeats = ref(0);
const subscriptionStatus = ref<string | null>(null);
const renewalDate = ref<string | null>(null);
const cancelAt = ref<string | null>(null);
const trialEnd = ref<string | null>(null);

const isSubscriptionLoading = ref(false);

const invoices = ref<InvoiceItem[]>([]);
const hasMoreInvoices = ref(false);
const isInvoicesLoading = ref(false);
const lastInvoiceId = ref<string | undefined>(undefined);
const latestInvoicesRequestId = ref(0);

function formatRenewalDate(iso: string): string {
    return format(parseISO(iso), 'MMM d, yyyy');
}

function formatCurrency(amountCents: number, currency: string): string {
    return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency.toUpperCase()
    }).format(amountCents / 100);
}

function formatInvoiceDate(timestamp: number): string {
    return new Date(timestamp * 1000).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    });
}

function statusLabel(inv: InvoiceData): string {
    if (inv.is_upcoming) return 'Upcoming';
    const labels: Record<string, string> = {
        paid: 'Paid',
        open: 'Open',
        draft: 'Draft',
        void: 'Void',
        uncollectible: 'Uncollectible'
    };
    return labels[inv.status] ?? inv.status;
}

function invoiceLabel(inv: InvoiceData): string {
    if (inv.is_upcoming) return 'Next invoice';
    if (inv.created) return formatInvoiceDate(inv.created);
    return inv.id;
}

function toInvoiceItem(inv: InvoiceData): InvoiceItem {
    return {
        id: inv.id,
        label: invoiceLabel(inv),
        statusLabel: statusLabel(inv),
        amountLabel: formatCurrency(inv.amount_due, inv.currency),
        stripeUrl: inv.hosted_invoice_url ?? ''
    };
}

async function fetchBillingData() {
    if (!tenantStore.tenantId) return;
    isSubscriptionLoading.value = true;
    try {
        const [, subscriptionData] = await Promise.all([
            accessContextStore.ensureLoaded(true),
            getSubscriptionStatus(tenantStore.tenantId).catch(() => null)
        ]);
        if (subscriptionData) {
            subscriptionStatus.value = subscriptionData.status;
            paidSeats.value = subscriptionData.quantity;
            renewalDate.value = subscriptionData.next_billing_date ?? null;
            cancelAt.value = subscriptionData.cancel_at ?? null;
            trialEnd.value = subscriptionData.trial_end ?? null;
        } else {
            subscriptionStatus.value = null;
            paidSeats.value = 0;
            renewalDate.value = null;
            cancelAt.value = null;
            trialEnd.value = null;
        }
    } catch (error) {
        console.error('Failed to fetch billing data:', error);
    } finally {
        isSubscriptionLoading.value = false;
    }
}

async function fetchInvoices(loadMore = false) {
    const tenantId = tenantStore.tenantId;
    if (!tenantId) return;
    if (loadMore && isInvoicesLoading.value) return;

    const requestId = ++latestInvoicesRequestId.value;
    isInvoicesLoading.value = true;
    try {
        const startingAfter = loadMore ? lastInvoiceId.value : undefined;
        const data = await getInvoices(tenantId, 10, startingAfter);
        const isStaleRequest =
            requestId !== latestInvoicesRequestId.value ||
            tenantId !== tenantStore.tenantId;
        if (isStaleRequest) return;
        const items = data.invoices.map(toInvoiceItem);

        if (loadMore) {
            invoices.value.push(...items);
        } else {
            invoices.value = items;
        }

        hasMoreInvoices.value = data.has_more;
        const realInvoices = data.invoices.filter(
            (i: InvoiceData) => !i.is_upcoming
        );
        const lastReal = realInvoices[realInvoices.length - 1];
        lastInvoiceId.value = lastReal?.id;
    } catch (error) {
        console.error('Failed to fetch invoices:', error);
    } finally {
        if (requestId === latestInvoicesRequestId.value) {
            isInvoicesLoading.value = false;
        }
    }
}

watch(
    () => tenantStore.tenantId,
    (tenantId) => {
        // Invalidate in-flight invoice requests before resetting local state.
        latestInvoicesRequestId.value += 1;
        subscriptionStatus.value = null;
        paidSeats.value = 0;
        renewalDate.value = null;
        cancelAt.value = null;
        trialEnd.value = null;
        invoices.value = [];
        hasMoreInvoices.value = false;
        isInvoicesLoading.value = false;
        lastInvoiceId.value = undefined;
        if (!tenantId) return;
        fetchBillingData();
        fetchInvoices();
    },
    { immediate: true }
);
</script>

<template>
    <div class="mt-0 space-y-6">
        <div class="space-y-2">
            <h2 class="text-xl font-semibold">Billing & invoices</h2>
            <p class="text-sm text-muted-foreground">
                Manage billing information, invoices and Stripe actions.
            </p>
        </div>

        <section
            v-if="accessContextStore.hasActiveSubscription"
            class="rounded-xl border divide-y"
        >
            <div
                class="grid gap-3 p-4 md:grid-cols-[220px_1fr_auto] md:items-center"
            >
                <div>
                    <p class="font-medium">Plan</p>
                    <p class="text-sm text-muted-foreground capitalize">
                        {{ currentSubscriptionPlan }}
                    </p>
                </div>
                <p class="text-sm text-muted-foreground">
                    <template v-if="isSubscriptionLoading">
                        Loading subscription details…
                    </template>
                    <template
                        v-else-if="currentSubscriptionPlan === 'enterprise'"
                    >
                        Custom enterprise billing
                    </template>
                    <template v-else-if="cancelAt">
                        Cancels on {{ formatRenewalDate(cancelAt) }}
                    </template>
                    <template
                        v-else-if="
                            subscriptionStatus === 'trialing' && trialEnd
                        "
                    >
                        Trial ends on {{ formatRenewalDate(trialEnd) }}
                    </template>
                    <template v-else-if="renewalDate">
                        Renewal on {{ formatRenewalDate(renewalDate) }}
                    </template>
                </p>
            </div>
            <div
                class="grid gap-3 p-4 md:grid-cols-[220px_1fr_auto] md:items-center"
            >
                <div>
                    <p class="font-medium">Subscription</p>
                    <p class="text-sm text-muted-foreground">
                        Manage payment method, billing details, period and
                        taxes.
                    </p>
                </div>
                <p class="text-sm text-muted-foreground">
                    Open Stripe customer portal to manage all subscription
                    settings.
                </p>
                <Button
                    variant="outline"
                    :disabled="stripeStore.isPortalLoading"
                    @click="stripeStore.openStripePortal"
                >
                    <template v-if="stripeStore.isPortalLoading"
                        >Opening portal…</template
                    >
                    <template v-else>Manage subscription</template>
                </Button>
            </div>
        </section>

        <section v-else class="rounded-xl border divide-y">
            <div
                v-for="i in 3"
                :key="i"
                class="grid gap-3 p-4 md:grid-cols-[220px_1fr_auto] md:items-center"
            >
                <div class="space-y-2">
                    <Skeleton class="h-4 w-24" />
                    <Skeleton class="h-3 w-16" />
                </div>
                <Skeleton class="h-3 w-48" />
                <Skeleton class="h-9 w-36 rounded-md" />
            </div>
        </section>

        <section
            v-if="accessContextStore.hasActiveSubscription"
            class="space-y-3"
        >
            <h3 class="text-lg font-semibold">Invoices</h3>

            <div
                v-if="isInvoicesLoading && invoices.length === 0"
                class="space-y-3"
            >
                <div
                    v-for="i in 3"
                    :key="i"
                    class="flex items-center gap-4 p-4"
                >
                    <div class="space-y-2 w-[240px]">
                        <Skeleton class="h-4 w-24" />
                        <Skeleton class="h-3 w-32" />
                    </div>
                    <Skeleton class="h-3 w-48 flex-1" />
                    <Skeleton class="h-9 w-32 rounded-md" />
                </div>
            </div>

            <p
                v-else-if="invoices.length === 0"
                class="text-sm text-muted-foreground"
            >
                No invoices yet.
            </p>

            <template v-else>
                <Table>
                    <TableHeader class="sr-only">
                        <TableRow>
                            <TableHead>Invoice</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Action</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        <TableRow v-for="invoice in invoices" :key="invoice.id">
                            <TableCell class="w-[240px]">
                                <p class="font-medium">
                                    {{ invoice.label }}
                                </p>
                                <p class="text-sm text-muted-foreground">
                                    {{ invoice.statusLabel }} ·
                                    {{ invoice.amountLabel }}
                                </p>
                            </TableCell>
                            <TableCell class="text-sm text-muted-foreground">
                                <template v-if="invoice.stripeUrl">
                                    Open invoice details in Stripe.
                                </template>
                            </TableCell>
                            <TableCell class="w-[200px] text-right">
                                <Button
                                    v-if="invoice.stripeUrl"
                                    variant="outline"
                                    class="gap-2"
                                    @click="openExternalLink(invoice.stripeUrl)"
                                >
                                    View invoice
                                    <ExternalLink class="h-4 w-4" />
                                </Button>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>

                <div v-if="hasMoreInvoices" class="flex justify-center pt-2">
                    <Button
                        variant="outline"
                        :disabled="isInvoicesLoading"
                        @click="fetchInvoices(true)"
                    >
                        <template v-if="isInvoicesLoading"> Loading… </template>
                        <template v-else>Load more invoices</template>
                    </Button>
                </div>
            </template>
        </section>
    </div>
</template>
